import { Key } from 'react'
import find from 'lodash/find'
import { observer } from 'mobx-react-lite'
import { generatePath, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import Styles from './WorkspaceDropdown.module.scss'
import { NEW_WORKSPACE_PATH } from 'paths'

import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useSession } from 'hooks/useSession'
import { useDefaultWorkspacePath } from 'utils/useDefaultWorkspacePath'
import { useRootStore } from 'stores/RootStore'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { RootState, SidebarTabs, UserRole, Workspace } from 'types'
import { WorkspaceService } from 'api/services/WorkspaceService'
import { UserAppPreferencesService } from 'api/services/UserAppPreferencesService'
import { UserService } from 'api/clients/UserService'
import { ThemeMode } from 'stores/Theme.types'
import { Pressable } from '@react-aria/interactions'
import { WorkspacesMenu } from './WorkspacesMenu'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { useQueryClient } from 'react-query'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import { updateModalsKey } from 'hooks/queries/updateModals/updateModalsKeys'
import { ChevronDown } from 'blaze-ui/icons'

const DEFAULT_WORKSPACE_LOGO_URL =
  'https://res.cloudinary.com/almanac/image/upload/v1763501162/blaze_assets/blaze-logo_vzlsxy.jpg'

export interface WorkspaceDropdownProps {
  selectedWorkspaceId: number
  workspaces: Workspace[]
}

export const WorkspaceDropdown = observer(({ selectedWorkspaceId, workspaces }: WorkspaceDropdownProps) => {
  const { auth, logout } = useSession()
  const { isMobile } = useResponsiveDesign()
  const { defaultWorkspacePath } = useDefaultWorkspacePath()
  const { workspaceTheme, leftSidebarStore } = useRootStore()
  const { setCurrentFolder } = useCurrentFolderStore()
  const history = useHistory()
  const selectedWorkspace = find(workspaces, { id: selectedWorkspaceId })
  const isSidebarOpen = useSelector((state: RootState) => state.sidebar.locked || state.floatingSidebar)
  const queryClient = useQueryClient()

  const leftSidebarWidth = leftSidebarStore.sidebarWidth

  const clearCalendarCache = () => {
    const queries = queryClient.getQueriesData<AlmanacFileCalendarItem[]>(calendarKeys.all)
    queries.forEach(([queryKey]) => {
      queryClient.setQueryData(queryKey, null)
      queryClient.cancelQueries(queryKey)
    })
  }

  const clearWorkspaceSpecificQueries = () => {
    clearCalendarCache()
    queryClient.invalidateQueries(updateModalsKey)
  }

  if (!selectedWorkspace) {
    return null
  }

  const handleAction = (key: Key, close: () => void) => {
    if (key.toString().startsWith('workspace')) {
      const destinationWorkspace = workspaces.find((w) => w.id === parseInt(key.toString().split('-')[1]))
      if (!destinationWorkspace) {
        return
      }
      UserAppPreferencesService.updateActiveTab(SidebarTabs.Organizational)
      UserService.setCurrentWorkspaceId(destinationWorkspace.id)
      setCurrentFolder(null)
      if (destinationWorkspace.is_new) {
        WorkspaceService.markAsSeen(destinationWorkspace.key)
      }
      clearWorkspaceSpecificQueries()
      history.push(
        defaultWorkspacePath({
          id: destinationWorkspace.id,
          guestReader: destinationWorkspace.role === UserRole.GUEST,
          homepageDocSlug: destinationWorkspace.homepage_doc_slug,
        }),
      )
    }
    if (key === 'newWorkspace') {
      history.push(generatePath(NEW_WORKSPACE_PATH))
    }
    if (key === 'signout') {
      logout()
    }
    if (isMobile && isSidebarOpen) WorkspaceService.hideSidebar()
    close()
  }

  return (
    <DropdownTrigger
      isDisabled={!auth}
      placement="bottom-start"
      popoverClassName={Styles.popover}
      __UNSAFE_popoverStyle={{
        ...workspaceTheme.palette,
        boxShadow: workspaceTheme.mode === ThemeMode.Dark ? '0px 8px 24px 0px rgba(19, 21, 26, 0.40)' : undefined,
      }}
    >
      <Pressable>
        <div className={Styles.triggerContainer} data-testid="workspaceDropdownTriggerBtn">
          {selectedWorkspace.app_logo_url ? (
            <div className={Styles.logoContainer}>
              <img src={selectedWorkspace.app_logo_url} alt="workspace logo" />
            </div>
          ) : (
            <div className={Styles.workspaceNameContainer}>
              <img
                className={Styles.workspaceLogoButton}
                src={selectedWorkspace.logo_url || DEFAULT_WORKSPACE_LOGO_URL}
                alt="workspace logo"
              />
              <span className={Styles.workspaceName} style={{ maxWidth: `calc(${leftSidebarWidth}px - 76px)` }}>
                {selectedWorkspace.name}
              </span>
            </div>
          )}

          <div className={Styles.chevronDownContainer}>
            <ChevronDown size={16} color="var(--dark-40)" />
          </div>
        </div>
      </Pressable>
      {(close) => (
        <WorkspacesMenu
          onAction={(key) => handleAction(key, close)}
          workspaces={workspaces}
          workspaceId={selectedWorkspaceId}
        />
      )}
    </DropdownTrigger>
  )
})
